import React, { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useIntl } from "react-intl";
import { msgs } from "../../msgs";
import { ReadablePromotionDetail } from "../../types";
import { ApprovalCard, CardHeader, MetaLabel, Section, StatePill } from "./shared";
import { describeInventoryScope, giveNightMessage, htmlToPlainText } from "./formatting";
import { useJoinAnd, useLocaleName } from "./locale-hooks";
import { useLocaleProvider } from "../../../locale-provider";
import {
  DeviceTypesValue,
  GeolocationValue,
  WindowRangesView,
  restrictionSentence,
} from "./restriction-views";

const bold = (chunks: ReactNode) => <strong className="font-bold">{chunks}</strong>;

/**
 * Rich "show full details" card for `getPromotion`/`getPromotionByTicker`, reusing the SAME
 * building blocks the create/update approval cards use (WindowRangesView, restrictionSentence,
 * GeolocationValue, describeInventoryScope, DeviceTypesValue, giveNightMessage) so a promotion's
 * data and wording read IDENTICALLY whether the user is being asked to approve a change or is just
 * looking up the promotion's current state. See `ReadablePromotionDetail`'s own doc comment for
 * where each field comes from and its token-cost story — this component never receives, and never
 * needs, the raw inventory ticker list.
 */
export function PromotionDetailView({
  content,
  propertyTicker,
}: {
  content: ReadablePromotionDetail;
  propertyTicker?: string;
}) {
  const intl = useIntl();
  const localeName = useLocaleName();
  const joinAnd = useJoinAnd();
  const { shortLocale } = useLocaleProvider();

  const isGiveNight = content.discountPeriodicity != null;
  const isFeatured = content.isFeatured === true;
  const dp = content.discountPeriodicity;

  const linkTicker = content.propertyTicker || propertyTicker;
  const translations = Array.isArray(content.translations) ? content.translations : [];
  const deviceTypes = Array.isArray(content.deviceTypes) ? content.deviceTypes : [];
  const codes = (Array.isArray(content.codes) ? content.codes : []).filter(c => !!c);
  const bookingWindows = Array.isArray(content.bookingWindows) ? content.bookingWindows : [];
  const travelWindows = Array.isArray(content.travelWindows) ? content.travelWindows : [];
  const closeBookingWindows = Array.isArray(content.closeBookingWindows)
    ? content.closeBookingWindows
    : [];
  const closeTravelWindows = Array.isArray(content.closeTravelWindows)
    ? content.closeTravelWindows
    : [];
  const hasRestrictions =
    content.minStay != null ||
    content.maxStay != null ||
    content.minNotice != null ||
    content.maxNotice != null;

  const lineCount = content.inventoryLineCount ?? 0;
  const hasScope = content.appliesToAllInventory === true || lineCount > 0;
  const specificScope = describeInventoryScope(content.inventoryScopeDescriptor, intl);
  const scopeLabel = content.appliesToAllInventory
    ? intl.formatMessage(msgs.approvalAllInventory)
    : specificScope ?? intl.formatMessage(msgs.approvalSelectedRates);

  const badge = isGiveNight
    ? intl.formatMessage(msgs.approvalGiveNightBadge, {
        discountedUnits: dp!.discountedUnits,
        everyUnits: dp!.applyEveryNumberOfUnits,
      })
    : isFeatured
    ? intl.formatMessage(msgs.approvalFeaturedBadge)
    : intl.formatMessage(msgs.approvalRateBadge, { rate: content.rate ?? "—" });

  const primaryTranslation =
    translations.find(t => t.locale?.toLowerCase().slice(0, 2) === shortLocale) ??
    translations.find(t => t.locale?.toLowerCase().slice(0, 2) === "en") ??
    translations[0];
  const otherTranslations = translations.filter(t => t !== primaryTranslation);

  const metaFields = [
    content.group && {
      key: "group",
      label: intl.formatMessage(msgs.approvalGroup),
      value: content.group.replace(/^GROUP_/, ""),
    },
    codes.length > 0 && {
      key: "code",
      label: intl.formatMessage(msgs.approvalPromoCode),
      value: codes.join(", "),
    },
    content.sharingType === "SHARED" && {
      key: "sharing",
      label: intl.formatMessage(msgs.fieldSharingType),
      value: intl.formatMessage(msgs.fieldSharingTypeShared),
    },
    content.parentPropertyTicker && {
      key: "parent",
      label: intl.formatMessage(msgs.fieldParentPropertyTicker),
      value: content.parentPropertyTicker,
    },
    content.maxBookableUnits != null &&
      content.maxBookableUnits > 0 && {
        key: "maxUnits",
        label: intl.formatMessage(msgs.fieldMaxBookableUnits),
        value: String(content.maxBookableUnits),
      },
    typeof content.showPopup === "boolean" && {
      key: "popup",
      label: intl.formatMessage(msgs.fieldShowPopup),
      value: intl.formatMessage(content.showPopup ? msgs.confirmYes : msgs.confirmNo),
    },
    typeof content.isCombinable === "boolean" && {
      key: "combinable",
      label: intl.formatMessage(msgs.fieldIsCombinable),
      value: intl.formatMessage(content.isCombinable ? msgs.confirmYes : msgs.confirmNo),
    },
  ].filter(Boolean) as { key: string; label: string; value: string }[];

  return (
    <ApprovalCard>
      <CardHeader
        title={
          linkTicker && content.id ? (
            <Link
              to={`/${linkTicker}/promotions/${content.id}`}
              className="inline-flex items-center gap-1 text-[#212121] hover:underline">
              {content.ticker ?? "—"}
              <em className="fa fa-external-link text-[10px]" />
            </Link>
          ) : (
            content.ticker ?? "—"
          )
        }
        badge={badge}
      />

      {(typeof content.isActive === "boolean" || typeof content.isVisible === "boolean") && (
        <Section label={intl.formatMessage(msgs.approvalStateChange)}>
          <div className="flex flex-wrap gap-2">
            {typeof content.isActive === "boolean" && (
              <StatePill icon={content.isActive ? "fa-check-circle" : "fa-minus-circle"}>
                {intl.formatMessage(
                  content.isActive ? msgs.approvalStateActive : msgs.approvalStateInactive
                )}
              </StatePill>
            )}
            {typeof content.isVisible === "boolean" && (
              <StatePill icon={content.isVisible ? "fa-eye" : "fa-eye-slash"}>
                {intl.formatMessage(
                  content.isVisible ? msgs.approvalStateVisible : msgs.approvalStateNotVisible
                )}
              </StatePill>
            )}
          </div>
        </Section>
      )}

      <Section label={intl.formatMessage(msgs.approvalSectionDiscount)}>
        {isGiveNight ? (
          <div className="text-[#212121] text-sm">
            {intl.formatMessage(giveNightMessage(dp!.criteria), {
              discountedUnits: dp!.discountedUnits,
              everyUnits: dp!.applyEveryNumberOfUnits,
              b: bold,
            })}
          </div>
        ) : isFeatured ? (
          <div className="text-[#212121] text-sm">
            {intl.formatMessage(
              hasScope ? msgs.approvalFeaturedWithScope : msgs.approvalFeaturedSitewide,
              { scope: scopeLabel, b: bold }
            )}
          </div>
        ) : (
          <div className="text-[#212121] text-sm">
            {intl.formatMessage(msgs.approvalDiscountScope, {
              rate: content.rate ?? "—",
              scope: scopeLabel,
              b: bold,
            })}
          </div>
        )}
        {!content.appliesToAllInventory && lineCount > 0 && (
          <span className="block min-w-[220px] min-h-[19px] text-[#949494] text-[14px] font-normal leading-none">
            {intl.formatMessage(msgs.approvalInventoryLines, { count: lineCount })}
          </span>
        )}
      </Section>

      {isGiveNight && (
        <Section label={intl.formatMessage(msgs.approvalGiveNightRepeatable)}>
          <span className="text-[#212121] text-sm">
            {intl.formatMessage(dp!.isRepeatable ? msgs.confirmYes : msgs.confirmNo)}
          </span>
        </Section>
      )}

      {hasRestrictions && (
        <Section label={intl.formatMessage(msgs.approvalRestrictionsTitle)}>
          <div className="flex flex-col gap-1.5">
            {content.minStay != null && (
              <span className="text-[#212121] text-sm break-words">
                {restrictionSentence(intl, msgs.approvalMinStay, content.minStay, "nights")}
              </span>
            )}
            {content.maxStay != null && (
              <span className="text-[#212121] text-sm break-words">
                {restrictionSentence(intl, msgs.approvalMaxStay, content.maxStay, "nights")}
              </span>
            )}
            {content.minNotice != null && (
              <span className="text-[#212121] text-sm break-words">
                {restrictionSentence(intl, msgs.approvalMinNotice, content.minNotice, "days")}
              </span>
            )}
            {content.maxNotice != null && (
              <span className="text-[#212121] text-sm break-words">
                {restrictionSentence(intl, msgs.approvalMaxNotice, content.maxNotice, "days")}
              </span>
            )}
          </div>
        </Section>
      )}

      {travelWindows.length > 0 && (
        <Section label={intl.formatMessage(msgs.approvalStayDates)}>
          <WindowRangesView windows={travelWindows} />
        </Section>
      )}
      {bookingWindows.length > 0 && (
        <Section label={intl.formatMessage(msgs.approvalBookingDates)}>
          <WindowRangesView windows={bookingWindows} />
        </Section>
      )}
      {closeTravelWindows.length > 0 && (
        <Section label={intl.formatMessage(msgs.approvalCloseTravelWindow)}>
          <WindowRangesView windows={closeTravelWindows} />
        </Section>
      )}
      {closeBookingWindows.length > 0 && (
        <Section label={intl.formatMessage(msgs.approvalCloseBookingWindow)}>
          <WindowRangesView windows={closeBookingWindows} />
        </Section>
      )}

      {deviceTypes.length > 0 && (
        <Section label={intl.formatMessage(msgs.approvalDeviceTypes)}>
          <DeviceTypesValue devices={deviceTypes} intl={intl} />
        </Section>
      )}

      {metaFields.length > 0 && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 pl-3 pr-4 py-3 bg-white">
          {metaFields.map(({ key, label, value }) => (
            <div key={key} className="flex flex-col gap-0.5 min-w-0">
              <MetaLabel>{label}</MetaLabel>
              <span className="text-[#212121] text-sm truncate">{value}</span>
            </div>
          ))}
        </div>
      )}

      {content.geolocation && (
        <div className="flex flex-col gap-1 min-w-0 pl-3 pr-4 py-3 bg-white">
          <MetaLabel>{intl.formatMessage(msgs.approvalCountries)}</MetaLabel>
          <GeolocationValue descriptor={content.geolocation} />
        </div>
      )}

      {translations.length > 0 && primaryTranslation && (
        <Section label={intl.formatMessage(msgs.approvalTranslations)}>
          <div className="flex flex-col gap-1">
            <span className="font-bold text-[#212121] text-sm">
              {localeName(primaryTranslation.locale)}
            </span>
            {primaryTranslation.name && (
              <span className="text-[#212121] text-sm break-words">
                <span className="font-semibold">{htmlToPlainText(primaryTranslation.name)}</span>
                {primaryTranslation.description
                  ? `: ${htmlToPlainText(primaryTranslation.description)}`
                  : ""}
              </span>
            )}
          </div>
          {otherTranslations.length > 0 && (
            <span className="font-bold text-[#212121] text-sm">
              {joinAnd(otherTranslations.map(t => localeName(t.locale)))}
            </span>
          )}
        </Section>
      )}
    </ApprovalCard>
  );
}
